// src/com/veh_info/VehInfoInputRental.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import { Button, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import { VzmInputBlock, ErrorHintBox } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { VehInfoDictionary } from './VehInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import PhoneField from '../../common/PhoneField';
import { useSnackbar } from 'notistack';
import { GET_VEH_INFO_BY_PLATES } from './VehInfoGql';
import { userInfoRepo } from '../../value/AppModel';

const InputMap = VehInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (VEH_INFO) 정보 생성 또는 편집 Dialog
 * Vehicle Info
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-04-21 15:50:25 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function VehInfoInputRental({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const [getVehInfoItemByPlates, responseItem] = useLazyQuery(GET_VEH_INFO_BY_PLATES, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option)},
        onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);


    const onCompleteGetItem = (data, option) => {
        let duplicate = false;

        if(data.vehInfoItem) {
            if(isEdit) {
                if(data.vehInfoItem.vehInfoSeq !== item.vehInfoSeq) {
                    duplicate = true;
                }
            }
            else duplicate = true;
        }

        if(duplicate) {
            addAlert({
                title: '차량번호 중복 확인',
                message: ['이미 등록된 차량입니다.'],
            });
        }
        else {
            enqueueSnackbar('등록할 수 있는 차량번호입니다.', { variant: AppStyle.mui.success, autoHideDuration: 1500 });
        }
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const onClickCheckDuplicate = () => {
        if(input.data.vehPlates) {
            getVehInfoItemByPlates({variables:{vehPlates:input.data.vehPlates}});
        }
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            title={isEdit ? '용차 정보 수정' : '용차 정보 등록'}
        >
            <DialogContent>
                <Box style={{padding: 10, fontWeight: 'bold', fontSize: '1.05rem'}}>
                    {sessionInfo.user.dscName}
                </Box>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.vehPlates}
                        value={input.data.vehPlates}
                        onChange={onChangeValue}
                    />
                    <Button
                        variant={AppStyle.mui.outlined}
                        color={AppStyle.mui.primary}
                        onClick={onClickCheckDuplicate}
                        style={{marginLeft: 5}}
                        disabled={!input.data.vehPlates || !input.changed}
                    >중복확인</Button>
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.vehTypeName}
                        value={input.data.vehTypeName}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.vehTonName}
                        value={input.data.vehTonName}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.driverName}
                        value={input.data.driverName}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <PhoneField
                        dict={InputMap.driverMobile}
                        value={input.data.driverMobile}
                        onChange={onChangeValue}
                        isMobile={true}
                    />
                </VzmInputBlock>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || responseItem.loading}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
